import dayjs from 'dayjs';
import { useMemo } from 'react';
import { InitiativeStatus } from '../../api/initiative-client/initiative-client.type';
import {
  Measure,
  TimeAllocationType,
  type MeasureMetadata,
  type TimeAllocation,
} from '../../api/work-periods-client/work-periods-client.type';
import { KEY_MEASURES } from '../../containers/process-analysis/process-analysis.constants';
import { ComparisonMode, Tab } from '../../containers/process-analysis/process-analysis.type';
import { useGlobalStore } from '../global-store/global-store';
import { useProcessAnalysisStore } from './process-analysis-store';
import { filterCustomMeasures, filterDeprecatedMeasures } from './process-analysis-store.helpers';
import type {
  ComparisonModeFilters,
  EntityFilters,
  FilterEntityKey,
  InitiativeOption,
} from './process-analysis-store.type';

/**
 * Hook that groups available measures by their measure_group property
 *
 * @returns {Record<string, MeasureMetadata[]>} An object where keys are measure group names and values are arrays of measures in that group
 * - Key measures are placed in "Key Measures" group at the top
 * - Measures without a group are placed in 'Other'
 * - Measures within each group are sorted alphabetically by title
 */
const useGroupedMeasures = (): Record<string, MeasureMetadata[]> => {
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  // Group measures by measure_group
  const groupedMeasures = useMemo(() => {
    const groups: Record<string, MeasureMetadata[]> = {};
    const keyMeasures: MeasureMetadata[] = [];
    const remainingMeasures: MeasureMetadata[] = [];

    // Separate key measures from other measures
    availableMeasures.forEach((measure) => {
      if (KEY_MEASURES.includes(measure.measure_name)) {
        keyMeasures.push(measure);
      } else {
        remainingMeasures.push(measure);
      }
    });

    // Create Key Measures group if we have any key measures
    if (keyMeasures.length > 0) {
      // Sort key measures to match the order in KEY_MEASURES constant
      keyMeasures.sort((a, b) => {
        const indexA = KEY_MEASURES.indexOf(a.measure_name);
        const indexB = KEY_MEASURES.indexOf(b.measure_name);
        return indexA - indexB;
      });
      groups['Key Measures'] = keyMeasures;
    }

    // Group remaining measures by measure_group
    remainingMeasures.forEach((measure) => {
      let group = measure.measure_group || 'Other';
      // Rename "process_analysis" to "Custom" for display
      if (group === 'process_analysis') {
        group = 'Custom';
      }
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(measure);
    });

    // Sort measures within each group by title (except Key Measures which is already sorted)
    Object.keys(groups).forEach((group) => {
      if (group !== 'Key Measures') {
        groups[group].sort((a, b) => a.measure_title.localeCompare(b.measure_title));
      }
    });

    return groups;
  }, [availableMeasures]);

  return groupedMeasures;
};

/**
 * Hook that determines the comparison mode based on the active tab and selected items
 *
 * @returns {ComparisonMode} The current comparison mode
 * - Returns ComparisonMode.Multiple if:
 *   - On Portfolios tab and at least 1 portfolio is selected along with the global portfolio
 *   - On Teams tab and 2+ teams are selected
 *   - On Boards tab and 2+ boards are selected
 * - Returns ComparisonMode.Single otherwise
 */
const useComparisonMode = (): ComparisonMode => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  switch (activeTab) {
    case Tab.Portfolios:
      return selectedPortfolios.length > 0 && portfolio?.id ? ComparisonMode.Multiple : ComparisonMode.Single;
    case Tab.Teams:
      return selectedTeams.length > 1 ? ComparisonMode.Multiple : ComparisonMode.Single;
    case Tab.Boards:
      return selectedBoards.length > 1 ? ComparisonMode.Multiple : ComparisonMode.Single;
    default:
      return ComparisonMode.Single;
  }
};

/**
 * Hook that returns an array of entity IDs based on the active tab and comparison mode
 *
 * @returns {string[]} Array of entity IDs
 * - For Portfolios tab:
 *   - In Multiple mode: Returns global portfolio ID + selected portfolio IDs
 *   - In Single mode: Returns only global portfolio ID
 * - For Teams tab: Returns selected team IDs
 * - For Boards tab: Returns selected board IDs
 * - Returns empty array for invalid tab
 */
const useEntities = (): string[] => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  const comparisonMode = useComparisonMode();

  const entities = useMemo(() => {
    switch (activeTab) {
      case Tab.Portfolios:
        if (comparisonMode === ComparisonMode.Multiple) {
          return portfolio?.id ? [portfolio.id, ...selectedPortfolios] : selectedPortfolios;
        }

        return portfolio?.id ? [portfolio.id] : [];
      case Tab.Teams:
        return selectedTeams;
      case Tab.Boards:
        return selectedBoards;
      default:
        return [];
    }
  }, [activeTab, comparisonMode, portfolio, selectedPortfolios, selectedTeams, selectedBoards]);

  return entities;
};

/**
 * Hook that returns the primary entity ID based on the active tab
 *
 * @returns {string | null} Primary entity ID or null
 * - For Portfolios tab: Returns global portfolio ID
 * - For Teams tab: Returns first selected team ID
 * - For Boards tab: Returns first selected board ID
 * - Returns null if no entity is selected or for invalid tab
 */
const usePrimaryEntity = (): string | null => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  const entity = useMemo(() => {
    switch (activeTab) {
      case Tab.Portfolios:
        return portfolio?.id || null;
      case Tab.Teams:
        return selectedTeams[0] || null;
      case Tab.Boards:
        return selectedBoards[0] || null;
      default:
        return null;
    }
  }, [activeTab, portfolio, selectedTeams, selectedBoards]);

  return entity;
};

/**
 * Hook that returns an array of available measure names (excludes custom measures)
 *
 * @returns {Measure[]} Array of available measure names
 */
const useAvailableMeasureNames = (): Measure[] => {
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  return availableMeasures
    .filter(filterDeprecatedMeasures)
    .filter(filterCustomMeasures)
    .map(({ measure_name }) => measure_name as Measure);
};

/**
 * Hook that returns an array of all available measure names including custom measures
 *
 * @returns {Measure[]} Array of all available measure names including custom
 */
const useAllAvailableMeasureNames = (): Measure[] => {
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  return availableMeasures.filter(filterDeprecatedMeasures).map(({ measure_name }) => measure_name as Measure);
};

/**
 * Returns the effective time allocation type for API calls.
 * When the user has explicitly set an allocation type via the sidebar filter,
 * that value takes precedence. Otherwise falls back to auto-computing from
 * the date range (existing behaviour, no regression).
 *
 * @return {TimeAllocationType} The effective time allocation type.
 */
const useTimeAllocation = (): TimeAllocationType => {
  const selectedAllocationType = useProcessAnalysisStore((state) => state.selectedAllocationType);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  if (selectedAllocationType !== null) {
    return selectedAllocationType;
  }

  const startDateObj = dayjs(startDate);
  const endDateObj = dayjs(endDate);
  const diff = endDateObj.diff(startDateObj, 'day') + 1;

  switch (true) {
    case diff < 25:
      return TimeAllocationType.Daily;
    case diff < 51:
      return TimeAllocationType.Weekly;
    case diff < 101:
      return TimeAllocationType.BiWeekly;
    default:
      return TimeAllocationType.Monthly;
  }
};

/**
 * Hook that returns the start and end dates from the store
 *
 * @returns {Object} Object containing start and end dates
 */
const useDateRange = (): { startDate: string; endDate: string } => {
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  return { startDate, endDate };
};

/**
 * Custom hook that returns the time allocations from the Process Analysis store
 *
 * @returns {TimeAllocation[]} Array of time allocations
 */
const useTimeAllocations = (): TimeAllocation[] => {
  const timeAllocations = useProcessAnalysisStore((state) => state.timeAllocations);

  return timeAllocations;
};

/**
 * Custom hook that returns entity IDs and their corresponding display names
 * Combines portfolios, teams, and boards based on current selections
 *
 * @returns {Object} An object containing entity IDs and their corresponding display names
 */
const useEntityNames = (): {
  entityIds: string[];
  entityNames: Record<string, string>;
} => {
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios);
  const teams = useGlobalStore((state) => state.teams);

  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);

  const entities = useEntities();

  const entityNames: Record<string, string> = {};

  // Map entity names based on the active tab
  entities.forEach((entityId) => {
    switch (activeTab) {
      case Tab.Portfolios:
        // Check if it's the global portfolio
        if (portfolio && entityId === portfolio.id) {
          entityNames[entityId] = portfolio.name;
          return;
        }
        // Check if it's a selected portfolio
        if (selectedPortfolios.includes(entityId)) {
          const portfolioObj = portfolios.find((p) => p.id === entityId);
          entityNames[entityId] = portfolioObj?.name || entityId;
          return;
        }
        break;

      case Tab.Teams:
        // Check if it's a selected team
        if (selectedTeams.includes(entityId)) {
          const teamObj = teams.find((t) => t.id === entityId);
          entityNames[entityId] = teamObj?.name || entityId;
          return;
        }
        break;

      case Tab.Boards:
        // Check if it's a selected board - map board names using subprojects
        if (selectedBoards.includes(entityId)) {
          const subprojects = teams?.map((t) => t.subprojects).flat();
          const boardObj = subprojects?.find((b) => b.id === entityId);
          entityNames[entityId] = boardObj?.name || entityId;
          return;
        }
        break;

      default:
        break;
    }

    // Fallback to entity ID
    entityNames[entityId] = entityId;
  });

  return {
    entityIds: entities,
    entityNames,
  };
};

/**
 * Hook that generates the filter entity key for the current primary entity
 *
 * @returns {FilterEntityKey | null} The filter entity key or null if no primary entity
 */
const useCurrentEntityKey = (): FilterEntityKey | null => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const primaryEntity = usePrimaryEntity();

  return useMemo(() => {
    if (!primaryEntity) {
      return null;
    }

    return `${activeTab}:${primaryEntity}` as FilterEntityKey;
  }, [activeTab, primaryEntity]);
};

/**
 * Hook that returns the current entity filters for single mode or defaults
 *
 * @returns {EntityFilters} Current entity filters or default values
 */
const useCurrentEntityFilters = (): EntityFilters => {
  const singleModeEntityFilters = useProcessAnalysisStore((state) => state.singleModeEntityFilters);
  const comparisonMode = useComparisonMode();
  const entityKey = useCurrentEntityKey();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single && entityKey && singleModeEntityFilters[entityKey]) {
      return singleModeEntityFilters[entityKey];
    }

    return {
      selectedMeasures: [],
      selectedMeasureTrends: [],
      selectedInitiativeStatuses: [],
      selectedInitiativeCompletions: [],
      selectedInitiativeCompletionTrends: [],
      selectedInitiativeFocus: [],
      selectedInitiativeFocusTrends: [],
      selectedCapacityTeams: [],
      touched: {
        selectedMeasures: false,
        selectedMeasureTrends: false,
        selectedInitiativeStatuses: false,
        selectedInitiativeCompletions: false,
        selectedInitiativeCompletionTrends: false,
        selectedInitiativeFocus: false,
        selectedInitiativeFocusTrends: false,
        selectedCapacityTeams: false,
      },
    };
  }, [singleModeEntityFilters, comparisonMode, entityKey]);
};

/**
 * Hook to get entity filters for the current entity, returning null if no cached filters exist
 * This is useful for checking if entity-specific filters have been previously set
 *
 * @returns {EntityFilters | null} Current entity filters or null if no cached filters exist
 */
const useEntityFilters = (): EntityFilters | null => {
  const singleModeEntityFilters = useProcessAnalysisStore((state) => state.singleModeEntityFilters);
  const comparisonMode = useComparisonMode();
  const entityKey = useCurrentEntityKey();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single && entityKey) {
      return singleModeEntityFilters[entityKey] || null;
    }

    return null;
  }, [singleModeEntityFilters, comparisonMode, entityKey]);
};

/**
 * Hook to check if a specific filter was touched by the user
 *
 * @param {keyof NonNullable<EntityFilters['touched']>} filterField - The filter field to check
 * @returns {boolean} True if the filter was touched by the user, false otherwise
 */
const useFilterWasTouched = (filterField: keyof NonNullable<EntityFilters['touched']>): boolean => {
  const entityFilters = useEntityFilters();

  return useMemo(() => {
    return Boolean(entityFilters?.touched?.[filterField]);
  }, [entityFilters?.touched, filterField]);
};

/**
 * Hook that returns selected measures based on comparison mode
 *
 * @returns {Measure[]} Selected measures
 */
const useSelectedMeasures = (): Measure[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedMeasures;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedMeasures]);
};

/**
 * Hook that returns selected measure trends based on comparison mode
 *
 * @returns {Measure[]} Selected measure trends
 */
const useSelectedMeasureTrends = (): Measure[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedMeasureTrends;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedMeasureTrends]);
};

/**
 * Hook that returns selected initiative statuses based on comparison mode
 *
 * @returns {InitiativeStatus[]} Selected initiative statuses
 */
const useSelectedInitiativeStatuses = (): InitiativeStatus[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedInitiativeStatuses;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedInitiativeStatuses]);
};

/**
 * Hook that returns selected initiative completions based on comparison mode
 *
 * @returns {InitiativeOption[]} Selected initiative completions
 */
const useSelectedInitiativeCompletions = (): InitiativeOption[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedInitiativeCompletions;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedInitiativeCompletions]);
};

/**
 * Hook that returns selected initiative completion trends based on comparison mode
 *
 * @returns {InitiativeOption[]} Selected initiative completion trends
 */
const useSelectedInitiativeCompletionTrends = (): InitiativeOption[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedInitiativeCompletionTrends;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedInitiativeCompletionTrends]);
};

/**
 * Hook that returns selected initiative focus based on comparison mode
 *
 * @returns {InitiativeOption[]} Selected initiative focus
 */
const useSelectedInitiativeFocus = (): InitiativeOption[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedInitiativeFocus;
    }

    return [];
  }, [comparisonMode, entityFilters.selectedInitiativeFocus]);
};

/**
 * Hook that returns selected initiative focus trends based on comparison mode
 *
 * @returns {InitiativeOption[]} Selected initiative focus trends
 */
const useSelectedInitiativeFocusTrends = (): InitiativeOption[] => {
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single) {
      return entityFilters.selectedInitiativeFocusTrends;
    }
    return [];
  }, [comparisonMode, entityFilters.selectedInitiativeFocusTrends]);
};

/**
 * Hook that returns selected capacity teams based on comparison mode and active tab
 *
 * @returns {string[]} Selected capacity teams
 */
const useSelectedCapacityTeams = (): string[] => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const globalCapacityTeams = useProcessAnalysisStore((state) => state.selectedCapacityTeams);
  const comparisonMode = useComparisonMode();
  const entityFilters = useCurrentEntityFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Single && activeTab === Tab.Portfolios) {
      // Single mode in Portfolios tab - from entity filters
      return entityFilters.selectedCapacityTeams;
    } else if (comparisonMode === ComparisonMode.Multiple && activeTab === Tab.Teams) {
      // Multiple mode in Teams tab - from global state
      return globalCapacityTeams;
    }

    return [];
  }, [activeTab, comparisonMode, entityFilters.selectedCapacityTeams, globalCapacityTeams]);
};

/**
 * Hook that returns the current comparison mode filters for the active tab
 *
 * @returns {ComparisonModeFilters} Current comparison mode filters for the active tab
 */
const DEFAULT_COMPARISON_MODE_FILTERS: ComparisonModeFilters = {
  selectedMeasureOverTime: null,
  selectedMeasureOverTimeTrend: false,
  selectedMonthlyAveragesMeasures: [],
};

const useCurrentComparisonModeFilters = (): ComparisonModeFilters => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const comparisonModeFilters = useProcessAnalysisStore((state) => state.comparisonModeEntityFilters);

  return useMemo(() => {
    return comparisonModeFilters[activeTab] ?? DEFAULT_COMPARISON_MODE_FILTERS;
  }, [comparisonModeFilters, activeTab]);
};

/**
 * Hook that returns the selected measure over time.
 *
 * @returns {Measure | null} Selected measure over time, or null if not in Multiple mode
 */
const useSelectedMeasureOverTime = (): Measure | null => {
  const comparisonMode = useComparisonMode();
  const comparisonModeFilters = useCurrentComparisonModeFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Multiple) {
      return comparisonModeFilters.selectedMeasureOverTime;
    }

    return null;
  }, [comparisonMode, comparisonModeFilters.selectedMeasureOverTime]);
};

/**
 * Hook that returns the selected measure over time trend.
 *
 * @returns {boolean} Whether trend is shown for the selected measure over time, or false if not in Multiple mode
 */
const useSelectedMeasureOverTimeTrend = (): boolean => {
  const comparisonMode = useComparisonMode();
  const comparisonModeFilters = useCurrentComparisonModeFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Multiple) {
      return comparisonModeFilters.selectedMeasureOverTimeTrend;
    }

    return false;
  }, [comparisonMode, comparisonModeFilters.selectedMeasureOverTimeTrend]);
};

/**
 * Hook that returns the selected monthly averages measures.
 *
 * @returns {Measure[]} Selected monthly averages measures, or empty array if not in Multiple mode
 */
const useSelectedMonthlyAveragesMeasures = (): Measure[] => {
  const comparisonMode = useComparisonMode();
  const comparisonModeFilters = useCurrentComparisonModeFilters();

  return useMemo(() => {
    if (comparisonMode === ComparisonMode.Multiple) {
      return comparisonModeFilters.selectedMonthlyAveragesMeasures;
    }

    return [];
  }, [comparisonMode, comparisonModeFilters.selectedMonthlyAveragesMeasures]);
};

export {
  useAllAvailableMeasureNames,
  useAvailableMeasureNames,
  useComparisonMode,
  useDateRange,
  useEntities,
  useEntityNames,
  useFilterWasTouched,
  useGroupedMeasures,
  usePrimaryEntity,
  useSelectedCapacityTeams,
  useSelectedInitiativeCompletions,
  useSelectedInitiativeCompletionTrends,
  useSelectedInitiativeFocus,
  useSelectedInitiativeFocusTrends,
  useSelectedInitiativeStatuses,
  useSelectedMeasureOverTime,
  useSelectedMeasureOverTimeTrend,
  useSelectedMeasures,
  useSelectedMeasureTrends,
  useSelectedMonthlyAveragesMeasures,
  useTimeAllocation,
  useTimeAllocations,
};
